/**
 * /dev/components — Component showcase (P2 from ADR-0007).
 * Storybook-lite: все atoms + molecules с состояниями + примерами кода.
 *
 * Только для PreProd / dev — спрятать в production-build (флагом или роутом).
 */
import { useState } from 'react'
import {
  Button,
  Banner,
  Input,
  Checkbox,
  FormField,
  Spinner,
  Divider,
  ThemeToggle,
  toast,
  Modal,
  ModalBody,
  ModalFooter,
  ConfirmationDialog,
  Notification,
  PasswordStrengthMeter,
  EmailRequirementsList,
  Tooltip,
} from '@/components/ui'
import { Icon } from '@/components/icons/Icon'
import { Logo } from '@/components/auth/Logo'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { PinDots } from '@/components/auth/PinDots'
import { NumPad } from '@/components/auth/NumPad'

export function ComponentsShowcase() {
  const [pwd, setPwd] = useState('')
  const [checked, setChecked] = useState(false)
  const [pinDemo, setPinDemo] = useState('')
  const [pinError, setPinError] = useState(false)
  const [pinSuccess, setPinSuccess] = useState(false)
  const triggerPinShake = () => {
    setPinError(true)
    setTimeout(() => {
      setPinError(false)
      setPinDemo('')
    }, 400)
  }
  const triggerPinSuccess = () => {
    setPinSuccess(true)
    setTimeout(() => {
      setPinSuccess(false)
      setPinDemo('')
    }, 800)
  }
  const [modalOpen, setModalOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [notifVisible, setNotifVisible] = useState(true)

  return (
    <div className="min-h-screen bg-surface-canvas text-text-primary">
      <header className="sticky top-0 z-10 bg-surface-canvas border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Logo size="md" tone="brand" />
          <span className="text-text-muted text-sm">/ Components</span>
        </div>
        <div className="flex items-center gap-3">
          <a href="/dev/api" className="text-sm text-text-link font-medium">API docs →</a>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-12">
        <Section
          title="Button"
          description="4 variants × 3 sizes × 9 states. CVA + Radix Slot. ADR-0007."
        >
          <Group label="Variants (size md, default state)">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="link">Link</Button>
          </Group>

          <Group label="Sizes (variant primary)">
            <Button variant="primary" size="sm">Small</Button>
            <Button variant="primary" size="md">Medium</Button>
            <Button variant="primary" size="lg">Large</Button>
          </Group>

          <Group label="States">
            <Button variant="primary">Default</Button>
            <Button variant="primary" disabled>Disabled</Button>
            <Button variant="primary" loading>Loading</Button>
          </Group>

          <Group label="With icons">
            <Button variant="primary" leftIcon={<Icon name="check" size={16} />}>Save</Button>
            <Button variant="ghost" rightIcon={<Icon name="arrow-right" size={16} />}>Next</Button>
            <Button variant="secondary" leftIcon={<Icon name="arrow-left" size={16} />}>Back</Button>
          </Group>

          <Group label="As Anchor (asChild)">
            <Button asChild variant="primary"><a href="#">Link as primary button</a></Button>
            <Button asChild variant="link"><a href="#">Plain text link</a></Button>
          </Group>

          <CodeBlock>{`<Button variant="primary" size="md" loading={isPending}>Создать</Button>
<Button asChild variant="primary"><a href="/auth/login">Войти</a></Button>`}</CodeBlock>
        </Section>

        <Section
          title="Banner"
          description="Inline alert (info/success/warning/error). role=alert для warning/error."
        >
          <Group vertical>
            <Banner variant="info" title="Info" body="Описание информационного сообщения." />
            <Banner variant="success" title="Готово" body="Операция выполнена успешно." />
            <Banner variant="warning" title="Внимание" body="Подтверди действие, прежде чем продолжить." />
            <Banner variant="error" title="Ошибка" body="Не удалось выполнить запрос." />
            <Banner variant="info" body="Без заголовка — только тело сообщения." />
          </Group>

          <CodeBlock>{`<Banner variant="error" title="Неверный email" body="Проверь ввод" />`}</CodeBlock>
        </Section>

        <Section
          title="Input — 7 состояний"
          description="default · hover · focus · error · focus+error · disabled · success. Slots: leftSlot/rightSlot. Tooltip i-icon — hover/focus."
        >
          <Group vertical>
            <Group label="Состояния">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                <div>
                  <p className="text-xs text-text-muted mb-1">1. Default</p>
                  <Input placeholder="alex@company.ru" />
                </div>
                <div>
                  <p className="text-xs text-text-muted mb-1">2. Hover (наведи мышь)</p>
                  <Input placeholder="alex@company.ru" />
                </div>
                <div>
                  <p className="text-xs text-text-muted mb-1">3. Focus (нажми)</p>
                  <Input placeholder="alex@company.ru" autoFocus={false} />
                </div>
                <div>
                  <p className="text-xs text-text-muted mb-1">4. Error</p>
                  <Input value="фывф" readOnly state="error" />
                </div>
                <div>
                  <p className="text-xs text-text-muted mb-1">5. Focus + Error (жирная красная обводка)</p>
                  <Input defaultValue="фывф" state="error" />
                </div>
                <div>
                  <p className="text-xs text-text-muted mb-1">6. Disabled</p>
                  <Input value="readonly@frozen.ru" disabled readOnly />
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs text-text-muted mb-1">7. Success (проверка пройдена)</p>
                  <Input value="alex@company.ru" readOnly state="success" rightSlot={<Icon name="check-circle" size={16} className="text-status-success-text" />} />
                </div>
              </div>
            </Group>

            <Group label="Слоты + Tooltip i-icon">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                <Input
                  placeholder="Поиск..."
                  leftSlot={<Icon name="search" size={16} className="text-text-muted" />}
                />
                <Input
                  placeholder="alex@company.ru"
                  rightSlot={
                    <Tooltip content="Будет использован для входа. Только латиница." side="top">
                      <button type="button" aria-label="Подсказка" className="text-text-muted hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus/40 rounded">
                        <Icon name="info" size={16} />
                      </button>
                    </Tooltip>
                  }
                />
                <div className="flex items-center gap-2">
                  <Tooltip content="Внешняя i-иконка слева от поля — для длинных подсказок не отнимает площадь у input'а.">
                    <button type="button" aria-label="Подсказка" className="flex-shrink-0 text-text-muted hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus/40 rounded">
                      <Icon name="info" size={18} />
                    </button>
                  </Tooltip>
                  <Input placeholder="С внешней i-иконкой слева" />
                </div>
                <Input placeholder="Sizes: lg" size="lg" />
              </div>
            </Group>
          </Group>

          <CodeBlock>{`<Input state="success" rightSlot={<Icon name="check-circle" size={16}/>} />
<Input state="error" />  // или hasError для back-compat
<Input rightSlot={<Tooltip content="..."><button><Icon name="info"/></button></Tooltip>} />`}</CodeBlock>
        </Section>

        <Section
          title="PasswordInput"
          description="Input + eye toggle (a11y aria-pressed). Sub-component."
        >
          <Group vertical>
            <PasswordInput value={pwd} onChange={setPwd} placeholder="Минимум 10 символов" />
            <PasswordStrengthMeter value={pwd} alwaysShow />
            <p className="text-xs text-text-muted">В живых формах подсказка скрыта пока поле пустое или валидно. Тут показано через <code>alwaysShow</code>.</p>
          </Group>
        </Section>

        <Section
          title="PinDots + NumPad"
          description="Banking-pattern компоненты для PIN-flow (US-E1-009 redesign 2026-05-09). 4 dots + 3×4 numpad с tactile feedback. Используется на /auth/pin и /auth/pin-setup для mobile/tablet (touch only)."
        >
          <Group vertical>
            <p className="text-sm text-text-secondary">
              Введи 4 цифры через NumPad ↓ или с физической клавиатуры (0-9, Backspace).
              Когда PIN заполнен — клик «Trigger error» имитирует wrong-PIN: shake + clear.
            </p>
            <div className="my-6">
              <PinDots
                length={4}
                filled={pinDemo.length}
                error={pinError}
                success={pinSuccess}
              />
            </div>
            <NumPad
              onDigit={(d) => setPinDemo((p) => (p.length >= 4 ? p : p + d))}
              onDelete={() => setPinDemo((p) => p.slice(0, -1))}
              disabled={pinError || pinSuccess}
            />
            <div className="flex gap-3 justify-center mt-4 flex-wrap">
              <Button
                variant="primary"
                size="sm"
                disabled={pinDemo.length !== 4}
                onClick={triggerPinSuccess}
              >
                Trigger success (green)
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={pinDemo.length !== 4}
                onClick={triggerPinShake}
              >
                Trigger error (shake)
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setPinDemo('')}>
                Reset
              </Button>
            </div>
            <CodeBlock>{`<PinDots length={4} filled={pin.length} error={hasError} />
<NumPad
  onDigit={(d) => setPin(p => p + d)}
  onDelete={() => setPin(p => p.slice(0, -1))}
  disabled={isSubmitting}
/>

// CSS анимация в tailwind.config.ts:
//   keyframes.shake — translateX wave 0.36s
//   animation.shake — applied via PinDots error prop`}</CodeBlock>
          </Group>
        </Section>

        <Section
          title="Toast"
          description="Sonner-обёртка. Bottom-left, до 5 в стэке, авто-закрытие 5s (error 8s)."
        >
          <Group>
            <Button variant="primary" onClick={() => toast.success('Сохранено', 'Изменения применены')}>Success</Button>
            <Button variant="destructive" onClick={() => toast.error('Ошибка', 'Не удалось сохранить')}>Error</Button>
            <Button variant="secondary" onClick={() => toast.info('Подсказка', 'Можно отменить в настройках')}>Info</Button>
            <Button variant="secondary" onClick={() => toast.warning('Внимание', 'Лимит почти достигнут')}>Warning</Button>
          </Group>
          <CodeBlock>{`import { toast } from '@/components/ui'
toast.success('Сохранено', 'Изменения применены')`}</CodeBlock>
        </Section>

        <Section
          title="Modal"
          description="Radix Dialog wrapper. Sizes sm/md/lg, animated overlay, ESC + outside-click."
        >
          <Group>
            <Button variant="primary" onClick={() => setModalOpen(true)}>Открыть Modal</Button>
          </Group>
          <Modal
            open={modalOpen}
            onOpenChange={setModalOpen}
            title="Настройки компании"
            description="Здесь можно изменить параметры — изменения вступят в силу сразу."
            size="md"
          >
            <ModalBody>
              <p className="text-sm text-text-secondary">Содержимое модалки. Любой контент: формы, списки, описания.</p>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Отмена</Button>
              <Button variant="primary" onClick={() => { toast.success('Сохранено'); setModalOpen(false) }}>Сохранить</Button>
            </ModalFooter>
          </Modal>
        </Section>

        <Section
          title="ConfirmationDialog"
          description="Preset Modal для destructive actions. Default focus на Cancel."
        >
          <Group>
            <Button variant="destructive" onClick={() => setConfirmOpen(true)}>Удалить компанию</Button>
          </Group>
          <ConfirmationDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Удалить компанию?"
            description="Все данные будут потеряны без возможности восстановления."
            confirmLabel="Удалить"
            destructive
            loading={confirmLoading}
            onConfirm={async () => {
              setConfirmLoading(true)
              await new Promise((r) => setTimeout(r, 800))
              setConfirmLoading(false)
              setConfirmOpen(false)
              toast.success('Компания удалена')
            }}
          />
        </Section>

        <Section
          title="EmailRequirementsList"
          description="Inline-чек-лист + N-сегментная полоска (N = кол-во проверок). В живых формах рендерится только когда поле невалидно и не пустое."
        >
          <Group vertical>
            <p className="text-xs text-text-muted">Невалидный ввод (показано через <code>alwaysShow</code>):</p>
            <Input value="фывф" readOnly state="error" />
            <EmailRequirementsList value="фывф" alwaysShow />
            <div className="pt-2 border-t border-border-strong" />
            <p className="text-xs text-text-muted">Валидный — компонент скрыт (этот рендер тоже через alwaysShow для демо):</p>
            <Input value="alex@company.ru" readOnly state="success" rightSlot={<Icon name="check-circle" size={16} className="text-status-success-text" />} />
            <EmailRequirementsList value="alex@company.ru" alwaysShow />
          </Group>
        </Section>

        <Section
          title="Notification"
          description="Sticky banner с CTA + dismiss. В отличие от Toast — не авто-скрывается."
        >
          <Group vertical>
            {notifVisible ? (
              <Notification
                variant="warning"
                title="Подтверди email"
                description="Мы отправили письмо при регистрации. Перейди по ссылке, чтобы получить доступ."
                action={
                  <>
                    <Button variant="primary" size="sm">Отправить ещё раз</Button>
                    <Button variant="ghost" size="sm">Изменить email</Button>
                  </>
                }
                onDismiss={() => setNotifVisible(false)}
              />
            ) : (
              <Button variant="secondary" onClick={() => setNotifVisible(true)}>Показать снова</Button>
            )}
          </Group>
        </Section>

        <Section
          title="Checkbox"
          description="WCAG 2.5.5 Target Size AA (24×24 default)."
        >
          <Group>
            <Checkbox size="sm" />
            <Checkbox size="md" defaultChecked />
            <Checkbox size="lg" />
            <Checkbox disabled />
            <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            <span className="text-sm text-text-secondary">controlled: {checked ? 'true' : 'false'}</span>
          </Group>
        </Section>

        <Section
          title="FormField"
          description="Molecule: Label + Input + Error + a11y describedby auto-wiring."
        >
          <FormField id="demo-email" label="Email" required helper="Корпоративный адрес">
            {({ id, describedBy, hasError }) => (
              <Input id={id} type="email" placeholder="alex@company.ru" hasError={hasError} aria-describedby={describedBy} />
            )}
          </FormField>

          <FormField id="demo-name" label="Имя" error="Поле обязательно">
            {({ id, describedBy, hasError }) => (
              <Input id={id} type="text" hasError={hasError} aria-describedby={describedBy} />
            )}
          </FormField>
        </Section>

        <Section
          title="Spinner"
          description="Loader для in-flight операций."
        >
          <Group>
            <Spinner size="sm" tone="brand" />
            <Spinner size="md" tone="brand" />
            <Spinner size="lg" tone="brand" />
            <span className="bg-action-primary-bg p-3 rounded"><Spinner size="md" tone="onBrand" /></span>
            <Spinner tone="muted" />
          </Group>
        </Section>

        <Section
          title="Divider"
          description="Горизонтальный разделитель + опциональный inline label."
        >
          <Group vertical>
            <Divider />
            <Divider label="или email" />
          </Group>
        </Section>

        <Section
          title="Logo"
          description="Шрифтовой wordmark AI AgentCEO. Onest semibold ≥24px / Inter ≤20px."
        >
          <Group vertical>
            <div className="flex items-center gap-6 flex-wrap">
              <Logo size="sm" tone="brand" />
              <Logo size="md" tone="brand" />
              <Logo size="lg" tone="brand" />
            </div>
            <div className="bg-[#0A1020] p-6 rounded-lg flex items-center gap-6 flex-wrap">
              <Logo size="sm" tone="inverse" />
              <Logo size="md" tone="inverse" />
              <Logo size="lg" tone="inverse" />
            </div>
          </Group>
        </Section>

        <Section
          title="ThemeToggle"
          description="Light ↔ Dark cycle. Persists localStorage 'agentceo:theme'."
        >
          <Group>
            <ThemeToggle />
            <span className="text-sm text-text-secondary">Кликни для смены темы</span>
          </Group>
        </Section>

        <Section
          title="Coverage по 40 UI-элементам"
          description="Source of truth: knowledge-base/ux-research/40-ui-elements/coverage-status.md"
        >
          <Group vertical>
            <p className="text-sm text-text-secondary">
              ✅ Реализовано (10/40): Button · Checkbox · Input · Password field · Confirmation dialogue · Loader · Toast · Notification · Modal · Icon.
            </p>
            <p className="text-sm text-text-secondary">
              ➕ Сверх списка: Banner · FormField · EmailRequirementsList · PasswordStrengthMeter · Logo · ThemeToggle · Divider.
            </p>
            <p className="text-sm text-text-warning">
              🔴 Sprint 3 P1 (нужно для дашборда): Tabs · Select · Dropdown · Switch · Radio · TextArea · Search · Badge · Tag · Card · Tooltip · Skeleton · Breadcrumb · Sidebar (universal) · NavBar · Kebab · Widget · Progress bar (universal).
            </p>
            <p className="text-sm text-text-muted">
              🟤 Backlog P2: Accordion · Collapse · Pagination · Multiselect · Date picker · Wizard · Slider · Counter · Hamburger · Meatballs · Bento · Carousel · Chart.
            </p>
          </Group>
        </Section>

        <footer className="border-t border-border-strong pt-8 mt-16 text-sm text-text-muted">
          <p>AgentCEO Component Showcase — ADR-0007. Source: <code className="font-mono">app/src/components/ui/</code></p>
          <p className="mt-1">Documentation: <a href="/dev/api" className="text-text-link">API Reference</a></p>
        </footer>
      </main>
    </div>
  )
}

function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 min-w-0">
      <header>
        <h2 className="text-2xl font-display font-bold">{title}</h2>
        <p className="text-text-secondary text-sm">{description}</p>
      </header>
      <div className="space-y-4 bg-surface-raised border border-border-strong rounded-lg p-6 overflow-hidden">{children}</div>
    </section>
  )
}

function Group({ label, vertical, children }: { label?: string; vertical?: boolean; children: React.ReactNode }) {
  return (
    <div>
      {label && <p className="text-xs font-bold uppercase tracking-wider text-text-muted mb-2">{label}</p>}
      <div className={vertical ? 'flex flex-col gap-3 max-w-md' : 'flex flex-wrap items-center gap-3'}>
        {children}
      </div>
    </div>
  )
}

function CodeBlock({ children }: { children: string }) {
  // Фиксированная тёмная палитра как в IDE — читается одинаково в обеих темах.
  // whitespace-pre-wrap, чтобы длинные строки не уносили layout вправо;
  // min-w-0 нужен в случае flex-родителя (Group).
  return (
    <pre className="min-w-0 max-w-full bg-[#0B1120] border border-[#1F2937] text-[#E2E8F0] rounded-md p-3 text-xs font-mono leading-relaxed whitespace-pre-wrap break-words">
      <code>{children}</code>
    </pre>
  )
}
